import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Navbar } from './Navbar';
import { AnalysisLoadingScreen } from './AnalysisLoadingScreen';
import { UploadLoadingScreen } from './UploadLoadingScreen';
import './UnifiedHandwritingFlow.css';

export const UnifiedHandwritingFlow = ({
  user,
  analysisResult,
  onSignInClick,
  onUploadComplete,
  onGoHome
}) => {
  const { user: clerkUser } = useUser();
  const [showContent, setShowContent] = useState(false);
  // Skip education page when user signed in directly (no illusion test results)
  const [step, setStep] = useState(!analysisResult ? 2 : 1);
  const [stepTransition, setStepTransition] = useState(false);

  // Age State
  const [userAge, setUserAge] = useState('');

  // Upload State
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setShowContent(true), 100);
  }, []);

  const ageInitialized = useRef(false);
  useEffect(() => {
    if (ageInitialized.current) return;
    
    // 1. Try explicit age fields
    let savedAge = user?.age || user?.unsafeMetadata?.age || analysisResult?.age || analysisResult?.metadata?.age || analysisResult?.cards_data?.age;
    
    // 2. Try deriving from age_range (midpoint)
    if (!savedAge && (user?.age_range || user?.ageRange)) {
        const range = user.age_range || user.ageRange;
        const matches = range.match(/\d+/g);
        if (matches && matches.length === 2) {
            savedAge = Math.floor((parseInt(matches[0]) + parseInt(matches[1])) / 2);
        } else if (matches && matches.length === 1) {
            savedAge = parseInt(matches[0]);
        }
    }

    if (savedAge) {
      setUserAge(String(savedAge));
      ageInitialized.current = true;
    } else if (user?.dateOfBirth) {
      const birthDate = new Date(user.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      if (age > 0 && age < 80) {
        setUserAge(String(age));
        ageInitialized.current = true;
      }
    }
  }, [user, analysisResult]);

  const isAgeValid = userAge && parseInt(userAge, 10) >= 4 && parseInt(userAge, 10) <= 80;

  const getDominanceType = () => {
    if (!analysisResult) return 'Balanced';
    const leftPercentage = analysisResult.left_percentage || 50;
    const rightPercentage = analysisResult.right_percentage || 50;
    const difference = Math.abs(leftPercentage - rightPercentage);
    if (difference < 10) return 'Balanced Thinker';
    return leftPercentage > rightPercentage ? 'Left-Brain Oriented' : 'Right-Brain Oriented';
  };
  const dominanceType = getDominanceType();
  const userName = user?.name || user?.firstName || 'Friend';

  const goToUpload = () => {
    if (!isAgeValid) return;
    setStepTransition(true);
    setTimeout(() => {
      setStep(2);
      setStepTransition(false);
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 300);
  };

  const goBackToEducation = () => {
    setStepTransition(true);
    setTimeout(() => {
      setStep(1);
      setStepTransition(false);
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      setError('');
    }, 300);
  };

  // ==== UPLOAD LOGIC ====
  const handleCameraCapture = () => {
    if (cameraInputRef.current) cameraInputRef.current.click();
  };

  const handleFileUpload = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a JPG or PNG image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setError('');
      startUploadProcess(file);
    }
  };

  const resolveAge = () => {
    const parsed = parseInt(userAge, 10);
    if (!isNaN(parsed) && parsed >= 4) return parsed;
    // Derive from age_range midpoint as fallback
    const range = user?.age_range || user?.ageRange;
    if (range) {
      const matches = range.match(/\d+/g);
      if (matches && matches.length >= 2) return Math.floor((parseInt(matches[0]) + parseInt(matches[1])) / 2);
      if (matches && matches.length === 1) return parseInt(matches[0]);
    }
    return 25;
  };

  const startUploadProcess = async (file) => {
    const ageNum = resolveAge();
    if (clerkUser) {
      try {
        const today = new Date();
        const approxDob = new Date(today.getFullYear() - ageNum, today.getMonth(), today.getDate());
        await clerkUser.update({
          unsafeMetadata: {
            ...clerkUser.unsafeMetadata,
            age: ageNum,
            date_of_birth: approxDob.toISOString().split('T')[0],
          }
        });
      } catch (e) {
        console.warn('Failed to save age to Clerk:', e);
      }
    }
    if (user) user.age = ageNum;

    setUploadStatus('uploading');
    setUploadProgress(0);

    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) { clearInterval(progressInterval); return 100; }
        return prev + 10;
      });
    }, 200);

    try {
      const formData = new FormData();
      formData.append('handwriting_image', file);
      const safeUserData = {
        id: user.id, name: userName, email: user.email,
        occupation: user.occupation, gender: user.gender,
        age: ageNum, dateOfBirth: user.dateOfBirth
      };
      formData.append('user_data', JSON.stringify(safeUserData));
      formData.append('analysis_result', JSON.stringify(analysisResult));

      const uploadResponse = await fetch('/api/llm/analyze', { method: 'POST', body: formData });
      const uploadData = await uploadResponse.json();

      if (!uploadData.success) {
        if (uploadData.error === 'NOT_HANDWRITING') {
          throw new Error("Whoops! We couldn't detect any handwriting in that image. Please make sure you upload a clear photo of handwritten text.");
        }
        throw new Error(uploadData.error || uploadData.message || 'Upload failed');
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStatus('processing');
      await new Promise(resolve => setTimeout(resolve, 1500));
      setUploadStatus('complete');
      setTimeout(() => { onUploadComplete && onUploadComplete(uploadData); }, 1500);

    } catch (error) {
      console.error('Upload failed:', error);
      setError(error.message || 'Upload failed. Please try again.');
      setUploadStatus(null);
      setUploadProgress(0);
      clearInterval(progressInterval);
      // Reset file inputs so the same file can be selected again after an error
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleCancelUpload = () => {
    setUploadStatus(null);
    setUploadProgress(0);
    setError('');
  };

  return (
    <div className="welcome-personalized base-background">
      <Navbar currentUser={user} onSignInClick={onSignInClick} onGoHome={onGoHome} />

      <div className={`welcome-content ${showContent ? 'animate-in' : ''} ${stepTransition ? 'step-fade-out' : 'step-fade-in'}`} style={{ maxWidth: '600px', transition: 'opacity 0.3s ease' }}>

        {/* ══════════════ PAGE 1: EDUCATION ══════════════ */}
        {step === 1 && (
          <>
            {/* Welcome Header */}
            <div style={{ textAlign: 'center', width: '100%', marginBottom: '4px' }}>
              <p style={{ margin: 0, fontSize: '15px', color: 'rgba(255,255,255,0.5)' }}>
                Welcome, <span style={{ color: 'white', fontWeight: 600 }}>{userName}</span>
              </p>
            </div>

            {/* AI Life Coach Intro Card */}
            <div className="dashboard-card" style={{ opacity: 1, transform: 'none', animation: 'none' }}>
              <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '36px', lineHeight: 1 }}>✨</div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: 'white', lineHeight: 1.3 }}>
                  Meet your <span style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>AI Life Coach</span>
                </h2>
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, maxWidth: '320px' }}>
                  A personal coach that truly understands <em>you</em> — your personality, strengths, and how your mind works.
                </p>
                <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {['Personality Insights', 'Growth Plans', 'Daily Coaching'].map((tag, i) => (
                    <span key={i} style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 600, background: ['rgba(168,85,247,0.15)', 'rgba(59,130,246,0.15)', 'rgba(236,72,153,0.15)'][i], color: ['#c084fc', '#93c5fd', '#f9a8d4'][i], border: `1px solid ${['rgba(168,85,247,0.25)', 'rgba(59,130,246,0.25)', 'rgba(236,72,153,0.25)'][i]}` }}>{tag}</span>
                  ))}
                </div>
              </div>

              <hr style={{ border: 'none', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', margin: '16px 0' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(168, 85, 247, 0.08)', borderRadius: '12px' }}>
                <span style={{ fontSize: '18px' }}>✍️</span>
                <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>
                  <strong style={{ color: '#c084fc' }}>It all starts with your handwriting.</strong> Upload a sample and our AI will decode your unique cognitive blueprint.
                </p>
              </div>
            </div>

            {/* Education Content */}
            <div className="glass-panel education-panel" style={{ width: '100%', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

              <h3 style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'center' }}>How it works</h3>

              {/* Fact Card */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', background: 'rgba(30, 20, 50, 0.6)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '16px', textAlign: 'left' }}>
                <div style={{ fontSize: '28px', flexShrink: 0, filter: 'drop-shadow(0 0 10px rgba(236, 72, 153, 0.3))' }}>🧠</div>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '0.8rem', color: '#a855f7', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Did you know?</h3>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>
                    According to graphology, your handwriting can reveal over <strong>5,000 personality traits</strong>—from emotional stability to hidden talents.
                  </p>
                </div>
              </div>

              {/* Steps */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {[
                  { num: '01', text: <>Write <strong>3 simple lines</strong> on any paper.</> },
                  { num: '02', text: <>Content <strong>doesn't matter</strong>. Just write whatever.</> },
                  { num: '03', text: <>Snap a photo and <strong>upload</strong>.</> }
                ].map((s, i) => (
                  <div key={i} style={{ padding: '14px 10px', background: 'rgba(25, 20, 35, 0.6)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'rgba(255,255,255,0.15)', marginBottom: '6px' }}>{s.num}</div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>{s.text}</p>
                  </div>
                ))}
              </div>

              {/* Privacy */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'rgba(15, 25, 35, 0.6)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px', textAlign: 'left' }}>
                <span style={{ fontSize: '1.1rem' }}>🔒</span>
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.4 }}>
                  <strong style={{ color: 'rgba(255,255,255,0.9)' }}>Privacy First:</strong> We analyze the patterns (slants, loops, pressure), not the words. Your image is processed securely and deleted instantly.
                </p>
              </div>
            </div>


            {/* Sticky Bottom: Age + Proceed Button */}
            <div className="hwf-sticky-bottom">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: !isAgeValid ? '#ec4899' : 'rgba(255,255,255,0.4)', transition: 'color 0.3s' }}>
                  {isAgeValid ? 'Your age' : 'Enter your age'}
                </span>
                <button onClick={() => setUserAge(prev => String(Math.max(4, (parseInt(prev, 10) || 20) - 1)))} className="age-stepper-btn">−</button>
                <input 
                  type="text" 
                  inputMode="numeric" 
                  pattern="[0-9]*" 
                  value={userAge} 
                  onChange={e => setUserAge(e.target.value.replace(/[^0-9]/g, ''))} 
                  placeholder="??" 
                  className={`age-input ${!userAge ? 'pulse-border' : ''}`}
                  style={{ 
                    width: '60px', 
                    textAlign: 'center', 
                    fontSize: '22px', 
                    fontWeight: 800, 
                    background: 'rgba(255,255,255,0.08)', 
                    border: !isAgeValid ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.3)', 
                    borderRadius: '12px', 
                    padding: '8px 5px', 
                    color: 'white', 
                    outline: 'none', 
                    transition: 'all 0.3s',
                    boxShadow: !userAge ? '0 0 15px rgba(236, 72, 153, 0.3)' : 'none'
                  }} 
                />
                <button onClick={() => setUserAge(prev => String(Math.min(80, (parseInt(prev, 10) || 20) + 1)))} className="age-stepper-btn">+</button>
              </div>
              <button 
                className="proceed-upload-btn" 
                disabled={!isAgeValid} 
                onClick={goToUpload}
                style={{
                  opacity: isAgeValid ? 1 : 0.5,
                  filter: isAgeValid ? 'none' : 'grayscale(0.5)'
                }}
              >
                {isAgeValid ? 'Upload My Handwriting ✍️' : 'Enter Age to Continue'}
              </button>
            </div>
          </>
        )}

        {/* ══════════════ PAGE 2: UPLOAD ══════════════ */}
        {step === 2 && !uploadStatus && (
          <>
            {/* Back Button — only shown when user came through step 1 (illusion test flow) */}
            {analysisResult && (
              <button onClick={goBackToEducation} className="hwf-back-btn">
                <span>←</span> Back
              </button>
            )}

            {/* Upload Header */}
            <div style={{ textAlign: 'center', width: '100%', marginBottom: '8px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📸</div>
              <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white', margin: '0 0 8px' }}>
                Ready to <span style={{ color: '#a855f7' }}>discover</span> yourself?
              </h2>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                Upload a clear photo of your handwriting. We'll do the rest.
              </p>
            </div>

            {/* Inline age picker — only shown when age not already known */}
            {!isAgeValid && (
              <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '14px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.5)' }}>Your age</span>
                <button onClick={() => setUserAge(prev => String(Math.max(4, (parseInt(prev, 10) || 20) - 1)))} className="age-stepper-btn">−</button>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={userAge}
                  onChange={e => setUserAge(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="??"
                  className="age-input"
                  style={{ width: '56px', textAlign: 'center', fontSize: '20px', fontWeight: 800, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', padding: '6px 4px', color: 'white', outline: 'none' }}
                />
                <button onClick={() => setUserAge(prev => String(Math.min(80, (parseInt(prev, 10) || 20) + 1)))} className="age-stepper-btn">+</button>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>optional</span>
              </div>
            )}

            {/* Error Card */}
            {error && (
              <div className="animate-fade-in" style={{ textAlign: 'center', width: '100%', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)' }}>
                <div style={{ fontSize: '32px' }}>😅</div>
                <h3 style={{ margin: 0, color: '#f87171', fontSize: '16px', fontWeight: 'bold' }}>Hold up!</h3>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: '14px', lineHeight: '1.5' }}>{error}</p>
                <button onClick={() => setError('')} style={{ marginTop: '8px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', border: 'none', borderRadius: '24px', padding: '10px 24px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)', transition: 'transform 0.2s ease' }}
                  onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                  onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >Try Again</button>
              </div>
            )}

            {/* Upload Methods */}
            {!error && (
              <div className="upload-methods" style={{ maxWidth: '100%', width: '100%' }}>
                <button className="upload-method camera-method" onClick={handleCameraCapture} style={{ padding: '1.25rem', width: '100%' }}>
                  <div className="method-icon camera-icon">📷</div>
                  <div className="method-info">
                    <h3 className="method-title">Snap a Photo</h3>
                    <p className="method-label">INSTANT VIBES</p>
                    <p className="method-description">Capture your writing right now.</p>
                  </div>
                </button>

                <button className="upload-method" onClick={handleFileUpload} style={{ padding: '1.25rem', width: '100%' }}>
                  <div className="method-icon upload-icon">📁</div>
                  <div className="method-info">
                    <h3 className="method-title">Upload from Roll</h3>
                    <p className="method-label">GALLERY</p>
                    <p className="method-description">Pick an existing photo.</p>
                  </div>
                </button>
              </div>
            )}

            {/* Upload Tips */}
            <div style={{ width: '100%', padding: '16px 20px', background: 'rgba(25, 20, 35, 0.5)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', marginTop: '8px' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '0.75rem', color: '#a855f7', textTransform: 'uppercase', letterSpacing: '0.1em' }}>💡 Tips for best results</h4>
              <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
                <li>Use <strong style={{ color: 'rgba(255,255,255,0.85)' }}>unlined white paper</strong> if possible</li>
                <li>Write in your <strong style={{ color: 'rgba(255,255,255,0.85)' }}>natural, relaxed style</strong></li>
                <li>Ensure <strong style={{ color: 'rgba(255,255,255,0.85)' }}>good lighting</strong> with no shadows</li>
              </ul>
            </div>
          </>
        )}

        {/* ══════════════ PROCESSING VIEWS ══════════════ */}
        {uploadStatus && (
          <div className="upload-progress-container h-full flex flex-col justify-center" style={{ width: '100%', padding: '20px 0' }}>
            {uploadStatus === 'uploading' ? (
              <div className="w-full animate-fade-in"><UploadLoadingScreen progress={uploadProgress} /></div>
            ) : (
              <div className="animate-fade-in w-full"><AnalysisLoadingScreen /></div>
            )}
            <button onClick={handleCancelUpload} className="cancel-upload-btn" disabled={uploadStatus === 'complete'} style={{ marginTop: '30px' }}>Cancel Upload</button>
          </div>
        )}

        {/* Hidden Inputs */}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleFileChange} style={{ display: 'none' }} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={{ display: 'none' }} />
      </div>
    </div>
  );
};
